/* Anirveda store chrome (Foxtale clone): announcement carousel, mobile menu, cart drawer.
   The cart drawer listens to the standard cart events (Horizon product forms and the ani-fx page both send them),
   re-renders itself with the Section Rendering API and opens after an add. */
import { StandardEvents } from '@shopify/events';

const shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const qa = (root, sel) => Array.from(root.querySelectorAll(sel));
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------- Announcement bar ---------- */
function initAnnouncement(bar) {
  if (bar.dataset.ready) return;
  bar.dataset.ready = '1';
  try { if (sessionStorage.getItem('ani-fxc-ann-closed') === bar.dataset.key) { bar.hidden = true; return; } } catch (e) {}
  const track = bar.querySelector('.ani-fxc-ann__track');
  const slides = qa(bar, '.ani-fxc-ann__slide');
  let i = 0;
  let timer;
  const go = (n) => { i = (n + slides.length) % slides.length; track.style.transform = `translateX(${-100 * i}%)`; };
  const play = () => {
    clearInterval(timer);
    const every = Number(bar.dataset.speed || 4) * 1000;
    if (slides.length > 1 && !reduceMotion) timer = setInterval(() => go(i + 1), every);
  };
  let startX = null;
  track.addEventListener('pointerdown', (e) => { startX = e.clientX; });
  track.addEventListener('pointerup', (e) => {
    if (startX === null) return;
    const dx = e.clientX - startX;
    startX = null;
    if (Math.abs(dx) > 30) { go(i + (dx < 0 ? 1 : -1)); play(); }
  });
  bar.querySelector('.ani-fxc-ann__close')?.addEventListener('click', () => {
    bar.hidden = true;
    clearInterval(timer);
    try { sessionStorage.setItem('ani-fxc-ann-closed', bar.dataset.key); } catch (e) {}
  });
  play();
}

/* ---------- Sheets (mobile menu, cart) ---------- */
let lastFocus = null;
function openSheet(sheet) {
  if (!sheet) return;
  lastFocus = document.activeElement;
  sheet.dataset.open = 'true';
  sheet.setAttribute('aria-hidden', 'false');
  document.body.classList.add('ani-fxc-lock');
  setTimeout(() => sheet.querySelector('[data-fxc-close]')?.focus(), 50);
}
function closeSheet(sheet) {
  if (!sheet || sheet.dataset.open !== 'true') return;
  sheet.dataset.open = 'false';
  sheet.setAttribute('aria-hidden', 'true');
  if (!document.querySelector('[data-fxc-sheet][data-open="true"]')) document.body.classList.remove('ani-fxc-lock');
  lastFocus?.focus?.();
}

/* ---------- Cart drawer ---------- */
const cartEl = () => document.querySelector('[data-fxc-cart]');

async function refreshCart(open) {
  const cart = cartEl();
  if (!cart) return;
  const id = cart.dataset.sectionId;
  const body = cart.querySelector('.ani-fxc-cart__body');
  body?.setAttribute('aria-busy', 'true');
  try {
    const res = await fetch(`${shopRoot}?sections=${encodeURIComponent(id)}`, { headers: { Accept: 'application/json' } });
    const json = await res.json();
    const doc = new DOMParser().parseFromString(json[id], 'text/html');
    const fresh = doc.querySelector('[data-fxc-cart]');
    if (fresh) {
      ['.ani-fxc-cart__body', '.ani-fxc-cart__foot', '.ani-fxc-cart__strip-wrap'].forEach((sel) => {
        const a = cart.querySelector(sel);
        const b = fresh.querySelector(sel);
        if (a && b) a.innerHTML = b.innerHTML;
      });
      const count = fresh.dataset.count || '0';
      qa(document, '[data-fxc-count]').forEach((el) => { el.textContent = count; el.hidden = count === '0'; });
    }
  } catch (e) {
    console.warn('[ani-fxc] cart refresh failed', e);
  } finally {
    cart.querySelector('.ani-fxc-cart__body')?.removeAttribute('aria-busy');
  }
  if (open) openSheet(cart);
}

async function changeLine(line, quantity) {
  await fetch(`${shopRoot}cart/change.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ line: Number(line), quantity: Number(quantity) }),
  });
  await refreshCart(false);
}

async function addVariant(id) {
  const res = await fetch(`${shopRoot}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
  });
  await res.json();
  await refreshCart(false);
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const isAdd = event.action === 'add';
  const done = event.promise ? event.promise.then(() => {}, () => {}) : Promise.resolve();
  done.then(() => refreshCart(isAdd));
});

/* ---------- Clicks and keys ---------- */
document.addEventListener('click', (e) => {
  const t = e.target instanceof Element ? e.target : null;
  if (!t) return;

  const opener = t.closest('[data-fxc-open]');
  if (opener) {
    e.preventDefault();
    const sheet = document.getElementById(opener.dataset.fxcOpen);
    if (sheet && sheet.matches('[data-fxc-cart]')) refreshCart(true);
    else openSheet(sheet);
    return;
  }
  const closer = t.closest('[data-fxc-close]');
  if (closer) { closeSheet(closer.closest('[data-fxc-sheet]')); return; }

  const step = t.closest('[data-fxc-qty]');
  if (step) { changeLine(step.dataset.line, step.dataset.fxcQty); return; }

  const up = t.closest('[data-fxc-add]');
  if (up) {
    up.setAttribute('aria-busy', 'true');
    addVariant(up.dataset.fxcAdd).finally(() => up.removeAttribute('aria-busy'));
  }
});

document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') qa(document, '[data-fxc-sheet][data-open="true"]').forEach(closeSheet);
});

function init(root = document) {
  qa(root, '.ani-fxc-ann').forEach(initAnnouncement);
}
init();
document.addEventListener('shopify:section:load', (e) => init(e.target));
